import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

/**
 * Дополнительные фильтры страницы-категории в URL.
 *
 * `useListUrlState` знает только про `page`, `perPage` и `search` — то, что есть
 * у любого списка. Всё остальное (телефон, вид спорта, сортировка) у каждой
 * страницы своё, но живёт по тем же правилам: источник правды — URL, смена
 * фильтра возвращает на первую страницу, значение по умолчанию в адрес не
 * пишется.
 *
 * Текстовые фильтры объявляют `debounceMs`: пока пользователь печатает, значение
 * держится в черновике и в URL не попадает, иначе каждая буква давала бы запрос.
 */

export interface ListFilterParamDef {
  /** Значение, при котором параметр из адреса удаляется. По умолчанию — пустая строка. */
  defaultValue?: string;
  /** Текстовый ввод: сколько ждать после последнего нажатия перед записью в URL. */
  debounceMs?: number;
}

export interface ListFilterParams<K extends string> {
  /** Закоммиченные значения — то, что уходит в аргументы запроса. */
  values: Record<K, string>;
  /** То, что набрано в поле прямо сейчас: для полей ввода с дебаунсом. */
  drafts: Record<K, string>;
  setFilter: (key: K, value: string) => void;
}

/**
 * @param defs Описание набора фильтров. Ссылка должна быть стабильной —
 *   объявляйте константой на уровне модуля, как `mapSearchToArgs` у `usePagedList`.
 */
export function useListFilterParams<K extends string>(defs: Record<K, ListFilterParamDef>): ListFilterParams<K> {
  const [searchParams, setSearchParams] = useSearchParams();

  const keys = useMemo(() => Object.keys(defs) as K[], [defs]);

  const readValues = useCallback(
    (params: URLSearchParams) =>
      keys.reduce(
        (acc, key) => {
          acc[key] = params.get(key) ?? defs[key].defaultValue ?? '';
          return acc;
        },
        {} as Record<K, string>,
      ),
    [defs, keys],
  );

  const values = useMemo(() => readValues(searchParams), [readValues, searchParams]);

  // ---- Черновики: то, что уже набрано, но ещё не доехало до URL -------------

  const [drafts, setDrafts] = useState(values);

  /**
   * Последнее, что в URL записали мы сами. Всё, что разошлось с ним, пришло
   * извне — «назад», клик по ссылке, сброс фильтров, — и черновик обязан
   * подстроиться под адрес, а не перезаписать его обратно.
   */
  const lastCommittedRef = useRef(values);

  useEffect(() => {
    const changed = keys.filter((key) => values[key] !== lastCommittedRef.current[key]);
    if (!changed.length) return;

    lastCommittedRef.current = values;
    setDrafts((prev) => {
      const next = { ...prev };
      changed.forEach((key) => {
        next[key] = values[key];
      });
      return next;
    });
  }, [keys, values]);

  const commit = useCallback(
    (entries: [K, string][]) => {
      lastCommittedRef.current = { ...lastCommittedRef.current };
      entries.forEach(([key, value]) => {
        lastCommittedRef.current[key] = value;
      });

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          entries.forEach(([key, value]) => {
            if (value === '' || value === (defs[key].defaultValue ?? '')) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });

          // Отфильтрованный список короче исходного — на седьмой странице
          // результата почти наверняка нет.
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [defs, setSearchParams],
  );

  useEffect(() => {
    const pending = keys.filter((key) => defs[key].debounceMs && drafts[key] !== lastCommittedRef.current[key]);
    if (!pending.length) return;

    const delay = Math.max(...pending.map((key) => defs[key].debounceMs ?? 0));
    const timer = setTimeout(() => commit(pending.map((key) => [key, drafts[key]])), delay);
    return () => clearTimeout(timer);
  }, [commit, defs, drafts, keys]);

  const setFilter = useCallback(
    (key: K, value: string) => {
      setDrafts((prev) => ({ ...prev, [key]: value }));
      // Селекты и переключатели уходят в URL сразу: ждать там нечего.
      if (!defs[key].debounceMs) commit([[key, value]]);
    },
    [commit, defs],
  );

  return { values, drafts, setFilter };
}
